import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import moment from 'moment';
import { toast } from 'react-toastify';
import { resendTriageNote, TriageModel } from 'data/api/triage.api';
import { latestOutboundNote, triageCreatedAt } from 'utils/triage';
import { MemberTriages } from '../useMemberTriages';
import { CARD, LINK_BUTTON_SX, Note } from './EventDetails';
import { TriageCard, TriageRow } from './TriageCard';
import { TriageDetail } from './TriageDetail';
import { TriageForm } from './TriageForm';

// The newest triages are the ones a rep acts on; everything older folds into
// compact rows so a long history does not push the rest of the rail away.
export const RECENT_COUNT = 3;

interface MonthGroup {
  key: string;
  label: string;
  triages: TriageModel[];
}

// Expects newest-first input and keeps that order.
const groupByMonth = (triages: TriageModel[]): MonthGroup[] => {
  const groups: MonthGroup[] = [];
  triages.forEach((triage) => {
    const at = moment(triageCreatedAt(triage));
    const key = at.format('YYYY-MM');
    const last = groups[groups.length - 1];
    if (last?.key === key) last.triages.push(triage);
    else groups.push({ key, label: at.format('MMMM YYYY'), triages: [triage] });
  });
  return groups;
};

const recipientWord = (count: number) =>
  count === 1 ? 'recipient' : 'recipients';

const MONTH_LABEL_SX = {
  fontSize: '13px',
  fontWeight: 700,
  color: '#7D818C',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

interface ITriagesSectionProps {
  /** The member the rail is showing. */
  userId: string;
  memberName?: string;
  triages: MemberTriages;
  /** The triage whose detail takes over the rail, if any. */
  openTriageId?: string;
  onSelectTriage: (triagesId?: string) => void;
  /** Whether the new-triage form takes over the rail. */
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
  /** Sends still on the wire, owned by the rail so they outlive this view. */
  inFlight: string[];
  onInFlight: (key: string, busy: boolean) => void;
}

export const TriagesSection: React.FC<ITriagesSectionProps> = ({
  userId,
  memberName,
  triages,
  openTriageId,
  onSelectTriage,
  creating,
  onCreatingChange,
  inFlight,
  onInFlight,
}) => {
  const [showEarlier, setShowEarlier] = useState<boolean>(false);
  // The triage this session just raised whose email did not go out. Only the
  // seed: once the refetched list contains it, its latest outbound note is
  // what the banner reads, so a Retry from anywhere — the banner or the
  // detail view — clears it. Kept out of the form because the form is gone by
  // then, and sending again there would raise a second triage.
  const [failedSend, setFailedSend] = useState<{
    triagesId: string;
    triagesNotesId: string;
  } | null>(null);
  const [retrying, setRetrying] = useState<boolean>(false);
  // Where keyboard focus goes back to when a takeover closes or the list
  // folds: the control that opened it, which unmounted when it did.
  const listRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<string>();

  const newestFirst = useMemo(
    () =>
      [...triages.triages].sort(
        (a, b) => triageCreatedAt(b) - triageCreatedAt(a),
      ),
    [triages.triages],
  );
  const recent = newestFirst.slice(0, RECENT_COUNT);
  const earlier = useMemo(
    () => groupByMonth(newestFirst.slice(RECENT_COUNT)),
    [newestFirst],
  );
  const earlierCount = Math.max(newestFirst.length - RECENT_COUNT, 0);

  const openFromList = (triagesId: string) => {
    returnFocusTo.current = triagesId;
    onSelectTriage(triagesId);
  };

  // A stale id can never match another family's triage — ids are unique and
  // the list is owner-guarded — so a member switch falls back to the list on
  // its own, with no reset to forget.
  const openTriage = triages.triages.find(
    (triage) => triage.triagesId === openTriageId,
  );

  // What the list currently says about the send this session just made. The
  // create response only seeds it: once the refetch has landed, the triage's
  // latest outbound note is the truth, so a Retry from the detail view clears
  // this banner too instead of leaving it offering to resend a delivered note.
  const currentFailure = (): typeof failedSend => {
    if (!failedSend) return null;
    const inList = triages.triages.find(
      (triage) => triage.triagesId === failedSend.triagesId,
    );
    // Not in the refetched list yet — the create response is the only account
    // of it there is, and the rep needs the failure now, not in a second.
    if (!inList) return failedSend;
    const note = latestOutboundNote(inList);
    return note?.emailStatus === 'failed'
      ? { triagesId: inList.triagesId, triagesNotesId: note.triagesNotesId }
      : null;
  };
  const failure = currentFailure();

  const listShowing = !openTriage && !creating;
  useEffect(() => {
    const key = returnFocusTo.current;
    if (!listShowing || !key) return;
    returnFocusTo.current = undefined;
    const target = listRef.current?.querySelector<HTMLElement>(
      `[data-focus-key="${key}"]`,
    );
    target?.focus();
    target?.scrollIntoView?.({ block: 'nearest' });
  }, [listShowing, showEarlier]);

  const toggleEarlier = (expand: boolean) => {
    returnFocusTo.current = expand
      ? newestFirst[RECENT_COUNT]?.triagesId
      : 'show-earlier';
    setShowEarlier(expand);
  };

  const onSent = (created: TriageModel) => {
    onCreatingChange(false);
    setFailedSend(null);
    const note = created.notes?.[0];
    const count = created.recipients?.length || 0;
    if (note?.emailStatus === 'sent') {
      toast.success(
        `Triage sent — email delivered to ${count} ${recipientWord(count)}`,
      );
    } else if (note?.emailStatus === 'failed') {
      setFailedSend({
        triagesId: created.triagesId,
        triagesNotesId: note.triagesNotesId,
      });
    } else {
      // Never say sent when it was not.
      toast.info('Triage created. The email is still sending.');
    }
    triages.reload();
  };

  const retrySend = async () => {
    if (!failure || retrying) return;
    setRetrying(true);
    try {
      const resent = await resendTriageNote(
        failure.triagesId,
        failure.triagesNotesId,
      );
      if (resent?.emailStatus === 'sent') {
        toast.success('Email sent.');
        // Drop the seed on a confirmed send so the banner cannot outlive it if
        // the reload below fails; the refetched note clears it otherwise.
        setFailedSend(null);
      } else {
        toast.error('The email failed to send again.');
      }
      triages.reload();
    } catch (error) {
      toast.error('The email could not be resent. Please try again.');
    }
    setRetrying(false);
  };

  return (
    <>
      {openTriage && (
        // Keyed by triage: without it, opening a second triage swaps the props
        // under a mounted composer and a reply drafted for the first one is
        // submitted under the second one's id, to the second one's recipients.
        <TriageDetail
          key={openTriage.triagesId}
          triage={openTriage}
          onBack={() => onSelectTriage(undefined)}
          onReload={triages.reload}
          onCommitRecipients={triages.commitRecipients}
          replyInFlight={inFlight.includes(`reply:${openTriage.triagesId}`)}
          onReplyingChange={(busy) =>
            onInFlight(`reply:${openTriage.triagesId}`, busy)
          }
        />
      )}

      {creating && (
        // Hidden rather than unmounted while a triage is open over it, so a
        // half-written triage survives a look at an earlier one.
        <Box sx={{ height: '100%', display: openTriage ? 'none' : 'block' }}>
          <TriageForm
            userId={userId}
            memberName={memberName}
            onCancel={() => onCreatingChange(false)}
            onSent={onSent}
            sendInFlight={inFlight.includes(`create:${userId}`)}
            onSendingChange={(busy) => onInFlight(`create:${userId}`, busy)}
          />
        </Box>
      )}

      {listShowing && (
        <Stack ref={listRef} sx={{ gap: '10px' }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              aria-hidden
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '7px',
                backgroundColor: '#356766',
                color: '#FFFFFF',
              }}
            >
              <CallMadeRoundedIcon sx={{ fontSize: '15px' }} />
            </Box>
            <Typography
              component="h3"
              sx={{ fontSize: '15px', fontWeight: 700, color: '#213B54' }}
            >
              Triages
            </Typography>
            {!!triages.triages.length && (
              <Chip
                label={triages.triages.length}
                size="small"
                sx={{
                  height: '22px',
                  backgroundColor: '#EFEDEB',
                  color: '#213B54',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              />
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRoundedIcon sx={{ fontSize: '16px' }} />}
              data-focus-key="new-triage"
              // Held while this family's create is still on the wire: when it
              // lands it closes this family's form, and a second one opened
              // after switching away and back would lose its draft with it.
              disabled={inFlight.includes(`create:${userId}`)}
              onClick={() => {
                returnFocusTo.current = 'new-triage';
                onCreatingChange(true);
              }}
              sx={{
                marginLeft: 'auto',
                height: '32px',
                borderRadius: '8px',
                borderColor: '#213B54',
                color: '#213B54',
                paddingX: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                ':hover': {
                  borderColor: '#213B54',
                  backgroundColor: '#F3F7F7',
                },
              }}
            >
              New triage
            </Button>
          </Stack>

          {!!failure && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                border: '1px solid #FDA29B',
                borderRadius: '10px',
                backgroundColor: '#FEF3F2',
                padding: '8px 12px',
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: '13px', color: '#B42318', flex: 1 }}>
                Email failed to send. The triage was saved.
              </Typography>
              <Button
                size="small"
                disabled={retrying}
                onClick={() => void retrySend()}
                sx={{
                  color: '#B42318',
                  textTransform: 'none',
                  fontWeight: 700,
                  paddingX: 0,
                  minWidth: 0,
                }}
              >
                {retrying ? 'Retrying' : 'Retry'}
              </Button>
            </Stack>
          )}

          {!!triages.error && (
            <Stack
              sx={{ ...CARD, padding: '10px 12px', alignItems: 'flex-start' }}
            >
              <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
                {triages.error}
              </Typography>
              {triages.retryable && (
                <Button
                  size="small"
                  onClick={triages.reload}
                  sx={LINK_BUTTON_SX}
                >
                  Try again
                </Button>
              )}
            </Stack>
          )}

          {triages.loading && !triages.triages.length && !triages.error && (
            <Stack sx={{ gap: '8px' }} aria-busy="true">
              {[0, 1].map((index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  height={132}
                  sx={{ borderRadius: '10px' }}
                />
              ))}
            </Stack>
          )}

          {!triages.loading && !triages.error && !triages.triages.length && (
            <Note>No triages yet for this family.</Note>
          )}

          {recent.map((triage) => (
            <TriageCard
              key={triage.triagesId}
              triage={triage}
              onOpen={openFromList}
            />
          ))}

          {earlierCount > 0 && !showEarlier && (
            <Button
              fullWidth
              data-focus-key="show-earlier"
              aria-expanded={false}
              onClick={() => toggleEarlier(true)}
              endIcon={<ExpandMoreRoundedIcon />}
              sx={{
                height: '40px',
                borderRadius: '10px',
                backgroundColor: '#EFEDEB',
                color: '#213B54',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                ':hover': { backgroundColor: '#E4E1DE' },
              }}
            >
              Show {earlierCount} earlier{' '}
              {earlierCount === 1 ? 'triage' : 'triages'}
            </Button>
          )}

          {earlierCount > 0 && showEarlier && (
            <>
              {earlier.map((group) => (
                <Stack
                  key={group.key}
                  component="section"
                  aria-label={group.label}
                  sx={{ gap: '6px', marginTop: '6px' }}
                >
                  <Typography sx={MONTH_LABEL_SX}>{group.label}</Typography>
                  <Stack sx={{ ...CARD, overflow: 'hidden' }}>
                    {group.triages.map((triage, index) => (
                      <TriageRow
                        key={triage.triagesId}
                        triage={triage}
                        onOpen={openFromList}
                        divider={index > 0}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
              <Button
                aria-expanded
                onClick={() => toggleEarlier(false)}
                endIcon={<ExpandLessRoundedIcon />}
                sx={{
                  alignSelf: 'center',
                  color: '#356766',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                Show less
              </Button>
            </>
          )}
        </Stack>
      )}
    </>
  );
};
